import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {codeScanningAlertPath} from '@github-ui/paths'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import {Banner, Button} from '@primer/react'
import {Suspense, useCallback} from 'react'
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import type {LazyRelationshipsSecurityAlertsListViewFragment$data} from './__generated__/LazyRelationshipsSecurityAlertsListViewFragment.graphql'
import type {LazyRelationshipsSecurityAlertsListViewQuery} from './__generated__/LazyRelationshipsSecurityAlertsListViewQuery.graphql'
import styles from './LazyRelationshipsSecurityAlertsListView.module.css'
import {RelationshipsListViewLoadingSkeleton, TEST_ID_SKELETON_ITEM} from './PaginatedRelationshipsListAllView'
import {SecurityAlertIcon} from './SecurityAlertIcon'

const PAGE_SIZE = 25
const SKELETON_ROWS = 3

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const LazyRelationshipsSecurityAlertsListViewFragment = graphql`
  fragment LazyRelationshipsSecurityAlertsListViewFragment on Issue
  @refetchable(queryName: "LazyRelationshipsSecurityAlertsListViewPaginatedQuery")
  @argumentDefinitions(cursor: {type: "String", defaultValue: null}, pageSize: {type: "Int"}) {
    securityAlerts(first: $pageSize, after: $cursor)
      @connection(key: "LazyRelationshipsSecurityAlertsListViewFragment__securityAlerts") {
      edges {
        node {
          number
          repository {
            nameWithOwner
          }
          status
          title
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const LazyRelationshipsSecurityAlertsListViewQueryGraphQL = graphql`
  query LazyRelationshipsSecurityAlertsListViewQuery($id: ID!, $cursor: String, $pageSize: Int) {
    issue: node(id: $id) {
      ...LazyRelationshipsSecurityAlertsListViewFragment @arguments(cursor: $cursor, pageSize: $pageSize)
    }
  }
`

export type LazyRelationshipsSecurityAlertsListViewProps = {
  issueId: string
  pageSize?: number
}

export function LazyRelationshipsSecurityAlertsListView({
  issueId,
  pageSize = PAGE_SIZE,
}: LazyRelationshipsSecurityAlertsListViewProps) {
  const loadingSkeletonComponent = <RelationshipsListViewLoadingSkeleton />

  return (
    <Suspense fallback={loadingSkeletonComponent}>
      <LazyRelationshipsSecurityAlertsListViewInternal issueId={issueId} pageSize={pageSize} />
    </Suspense>
  )
}

export function LazyRelationshipsSecurityAlertsListViewInternal({
  issueId,
  pageSize = PAGE_SIZE,
}: LazyRelationshipsSecurityAlertsListViewProps) {
  const queryData = useLazyLoadQuery<LazyRelationshipsSecurityAlertsListViewQuery>(
    LazyRelationshipsSecurityAlertsListViewQueryGraphQL,
    {id: issueId, pageSize},
  )

  const issueFragment = queryData.issue
  const {data, loadNext, isLoadingNext} = usePaginationFragment(
    LazyRelationshipsSecurityAlertsListViewFragment,
    issueFragment,
  )
  const issueData = data as LazyRelationshipsSecurityAlertsListViewFragment$data | null
  const onLoadMore = useCallback(() => loadNext(pageSize), [loadNext, pageSize])

  const emptyBanner = (
    <Banner variant="info">
      <Banner.Title>{LABELS.emptySecurityAlertsList}</Banner.Title>
    </Banner>
  )

  if (!issueFragment || !issueData) return emptyBanner

  const nodes = issueData.securityAlerts?.edges?.map(edge => edge?.node).filter(node => !!node)
  if (!nodes || nodes.length === 0) return emptyBanner

  const loadingSkeletonItems = []
  if (isLoadingNext) {
    for (let i = 0; i < SKELETON_ROWS; i += 1) {
      loadingSkeletonItems.push(
        <ListItem
          key={`skeleton-${i}`}
          {...testIdProps(TEST_ID_SKELETON_ITEM)}
          title={
            <ListItemTitle value={''}>
              <LoadingSkeleton height="20px" width="30ch" />
            </ListItemTitle>
          }
          metadata={
            <>
              <ListItemMetadata>
                <LoadingSkeleton variant="rounded" width="4ch" />
              </ListItemMetadata>
              <ListItemMetadata>
                <LoadingSkeleton variant="elliptical" width="20px" height="20px" />
              </ListItemMetadata>
            </>
          }
        />,
      )
    }
  }

  return (
    <>
      <ListView title={LABELS.relationNames.securityAlerts} variant="compact">
        {nodes.map(node => {
          const [owner, repo] = node.repository?.nameWithOwner.split('/') || []

          if (!owner || !repo || !node.number) return null

          return (
            <ListItem
              key={`${node.repository?.nameWithOwner}-${node.number}`}
              title={
                <ListItemTitle
                  value={node.title}
                  href={codeScanningAlertPath({
                    owner,
                    repo,
                    alertNumber: node.number,
                  })}
                  anchorClassName="markdown-title"
                />
              }
            >
              <ListItemLeadingContent>
                <ListItemLeadingVisual>
                  <SecurityAlertIcon status={node.status || 'OPEN'} />
                </ListItemLeadingVisual>
              </ListItemLeadingContent>

              <ListItemMainContent>
                <ListItemDescription>{`#${node.number}`}</ListItemDescription>
              </ListItemMainContent>
            </ListItem>
          )
        })}
        {isLoadingNext && loadingSkeletonItems}
      </ListView>
      {issueData.securityAlerts?.pageInfo?.hasNextPage && (
        <div className={styles.LazyRelationshipsSecurityAlertsListView_LoadMore}>
          <Button onClick={onLoadMore} disabled={isLoadingNext}>
            {LABELS.loadMoreItems}
          </Button>
        </div>
      )}
    </>
  )
}
